import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { usePageTitle, useMetaDescription } from '@/hooks/usePageTitle';
import { PublicShell } from '@/components/visual/PublicShell';
import { PageHeader, ActionLink } from '@/components/visual/PagePrimitives';
import { LocaleLink } from '@/components/LocaleLink';
import { Button } from '@/components/ui/button';
import { ProvenanceBadge } from '@/components/trust/ProvenanceBadge';
import { Calculator, Loader2 } from 'lucide-react';

const API = '/api';

function Table({ title, rows, provenance }) {
  return (
    <section className="methodology-table">
      <div className="methodology-table-head">
        <h2>{title}</h2>
        {provenance && <ProvenanceBadge provenance={provenance} />}
      </div>
      <dl x-excluded="true">
        {rows.map(([label, value]) => (
          <div key={label} className="methodology-row">
            <dt>{label}</dt>
            <dd>
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Public methodology page: the published engine configuration, no ISO text reproduced. */
export default function MethodologyPage() {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const [tco, setTco] = useState(null);
  const [sens, setSens] = useState(null);

  usePageTitle(t('methodologyPage.metaTitle'));
  useMetaDescription(t('methodologyPage.metaDescription'));

  useEffect(() => {
    axios.get(`${API}/engine/config`).then(res => setCfg(res.data)).catch(() => setCfg(false));
    axios.get(`${API}/engine/tco-model`).then(res => setTco(res.data)).catch(() => setTco(false));
    axios.get(`${API}/engine/sensitivity-model`).then(res => setSens(res.data)).catch(() => setSens(false));
  }, []);

  const d = t('methodologyPage.unitDays');
  const sizeRows = (obj, unit = '') => Object.entries(obj).map(([k, v]) => [t(`calcData.sizes.${k}`), `${v}${unit}`]);

  return (
    <PublicShell>
      <main id="main-content" tabIndex={-1} className="site-container disclosure-width public-main">
        <PageHeader title={t('methodologyPage.pageTitle')} description={t('methodologyPage.pageIntro')}
          eyebrow={t('trust.methodologyTitle')} testId="methodology-page-title" />

        {cfg === null && (
          <div className="flex items-center gap-2 text-sm text-[#66707A]" data-testid="methodology-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('methodologyPage.loading')}
          </div>
        )}
        {cfg === false && (
          <p className="text-sm text-[#B42318]" data-testid="methodology-error">
            {t('methodologyPage.loadError')}
          </p>
        )}

        {cfg && (
          <div className="space-y-5" data-testid="methodology-content">
            <div className="border border-[#DDE1E5] rounded-lg bg-white px-5 py-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm text-[#3A4249]">{t('trust.engineVersion')}</span>
                <span className="text-sm font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                      data-testid="methodology-engine-version">
                  {cfg.engineVersion}
                </span>
              </div>
              <p className="mt-2 text-xs text-[#8A929B]">
                {t('methodologyPage.authoritative')}
              </p>
            </div>

            <Table title={t('methodologyPage.tBaseEffort')} provenance="MODELLED"
                   rows={sizeRows(cfg.baseEfforts, ` ${d}`)} />
            <Table title={t('methodologyPage.tFloors')} provenance="MODELLED"
                   rows={sizeRows(cfg.effortFloors, ` ${d}`)} />
            <Table title={t('methodologyPage.tSector')} provenance="MODELLED"
                   rows={Object.entries(cfg.industryFactors).map(([k, v]) => [t(`calcData.industries.${k}`), `×${v.toFixed(2)}`])} />
            <Table title={t('methodologyPage.tInfra')} provenance="MODELLED"
                   rows={Object.entries(cfg.infrastructureFactors).map(([k, v]) => [t(`calcData.infra.${k}`), `×${v.toFixed(2)}`])} />
            <Table title={t('methodologyPage.tMaturity')} provenance="MODELLED"
                   rows={Object.entries(cfg.maturityFactors).map(([k, v]) => [`${t('methodologyPage.level')} ${k}`, `×${v.toFixed(2)}`])} />
            <Table title={t('methodologyPage.tReuse')} provenance="MODELLED"
                   rows={[
                     ...Object.entries(cfg.controlReuseReductions).map(([k, v]) => [k.toUpperCase(), `−${(v * 100).toFixed(0)}%`]),
                     [t('methodologyPage.tReuseMax'), `−${(cfg.maxReuseReduction * 100).toFixed(0)}%`],
                   ]} />
            <Table title={t('methodologyPage.tAudit')} provenance="ISO_DERIVED"
                   rows={[
                     [t('methodologyPage.tAuditModel'), t('methodologyPage.tAuditModelValue')],
                     [t('methodologyPage.tAuditCeiling'), t('methodologyPage.tAuditCeilingValue')],
                     [t('methodologyPage.tAuditMin'), `${cfg.minAuditDays} ${d}`],
                     [t('methodologyPage.tAuditRate'), `€${cfg.certBodyDayRate.toLocaleString()}`],
                     [t('methodologyPage.tAuditMultiSite'), t('methodologyPage.tAuditMultiSiteValue')],
                   ]} />
            <Table title={t('methodologyPage.tRates')} provenance="DEFAULT_ASSUMPTION"
                   rows={[
                     [t('methodologyPage.tRateInternal'), `€${cfg.defaultRates.internal.toLocaleString()}`],
                     [t('methodologyPage.tRateConsultant'), `€${cfg.defaultRates.consultant.toLocaleString()}`],
                   ]} />
            <Table title={t('methodologyPage.tTraining')} provenance="DEFAULT_ASSUMPTION"
                   rows={Object.entries(cfg.fixedCosts.training).map(([k, v]) => [t(`calcData.sizes.${k}`), `€${v.toLocaleString()}`])} />
            <Table title={t('methodologyPage.tTools')} provenance="DEFAULT_ASSUMPTION"
                   rows={Object.entries(cfg.fixedCosts.tools).map(([k, v]) => [t(`calcData.sizes.${k}`), `€${v.toLocaleString()}`])} />
            <Table title={t('methodologyPage.tPhases')} provenance="MODELLED"
                   rows={cfg.phases.map(p => [t(`calcData.phases.${p.id}`), `${p.percentOfEffort}%`])} />
            <Table title={t('methodologyPage.tConfidence')} provenance="ESTIMATED"
                   rows={cfg.confidence.bands.map(b => [
                     `${t(`trust.${b.level}`)} (${t('methodologyPage.scoreGte')} ${b.minScore})`,
                     t('methodologyPage.tConfidenceValue', {
                       low: Math.round(b.rangeLow * 100),
                       high: Math.round(b.rangeHigh * 100),
                     }),
                   ])} />

            <div className="border border-[#DDE1E5] rounded-lg bg-white px-5 py-4" data-testid="methodology-scenarios">
              <h3 className="text-sm font-semibold text-[#171A1D]">{t('methodologyPage.tScenarios')}</h3>
              <p className="mt-2 text-sm text-[#3A4249]">{t('methodologyPage.tScenariosBody')}</p>
            </div>

            {cfg.overridableAssumptions && (
              <div className="border border-[#DDE1E5] rounded-lg bg-white px-5 py-4" data-testid="methodology-overrides">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-[#171A1D]">{t('methodologyPage.tOverrides')}</h3>
                  <ProvenanceBadge provenance="USER_PROVIDED" />
                </div>
                <p className="mt-2 text-sm text-[#3A4249]">{t('methodologyPage.tOverridesBody')}</p>
                <p className="mt-2 text-xs text-[#8A929B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {cfg.overridableAssumptions.join(' · ')}
                </p>
              </div>
            )}

            {sens && (
              <Table title={t('methodologyPage.tSensitivity')} provenance="MODELLED"
                     rows={[
                       [t('methodologyPage.tSensitivityMethod'), t('methodologyPage.tSensitivityMethodValue')],
                       ...sens.variables.map(v => [t(`sensitivity.vars.${v.id}`), v.swing]),
                     ]} />
            )}

            {tco && (
              <>
                <Table title={t('methodologyPage.tTco')} provenance="DEFAULT_ASSUMPTION"
                       rows={[
                         [t('methodologyPage.tTcoModel'), tco.tcoModelVersion],
                         ...tco.assumptions.map(a => [
                           `${t(`tco.assumptions.${a.id}`)} (${t(`tco.units.${a.unit}`)})`,
                           String(a.default),
                         ]),
                         [t('methodologyPage.tTcoRecert'), t('methodologyPage.tTcoRecertValue')],
                         [t('methodologyPage.tTcoVerification'),
                          tco.methodologyVerificationOpen.map(id => t(`tco.assumptions.${id}`)).join(', ')],
                       ]} />
                <div className="border border-[#DDE1E5] rounded-lg bg-white px-5 py-4" data-testid="methodology-tco-notes">
                  <p className="text-sm text-[#3A4249]">{t('tco.note')}</p>
                  <p className="mt-2 text-xs text-[#66707A]">{t('tco.surveillanceNote')}</p>
                  <p className="mt-2 text-xs text-[#66707A]">{t('tco.recertExcluded')}</p>
                </div>
              </>
            )}

            <div className="border border-[#DDE1E5] rounded-lg bg-white px-5 py-4">
              <h3 className="text-sm font-semibold text-[#171A1D]">{t('methodologyPage.limitations')}</h3>
              <ul x-excluded="true" className="mt-2 space-y-1.5 text-sm text-[#3A4249] list-disc pl-5">
                <li>{t('methodologyPage.lim1')}</li>
                <li>{t('methodologyPage.lim2')}</li>
                <li>{t('methodologyPage.lim3')}</li>
                <li>{t('methodologyPage.lim4')}</li>
              </ul>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
                <ActionLink to="/calculator" data-testid="methodology-cta">
                  <Calculator className="w-4 h-4 mr-2" /> {t('homepage.primaryCta')}
                </ActionLink>
                <ActionLink to="/disclaimer" variant="outline" data-testid="methodology-disclaimer-link">
                  {t('landing.readDisclaimer')}
                </ActionLink>
            </div>
          </div>
        )}
      </main>
    </PublicShell>
  );
}
